<template>
    <VueFinalModal
        class="confirm-modal flex justify-center items-center px-4"
        content-class="confirm-modal-content"
        overlay-transition="vfm-fade"
        content-transition="vfm-fade"
    >
        <div class="bg-white rounded-lg overflow-hidden shadow-xl max-w-4xl w-full">
            <div class="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <h3 v-if="submissionSuccessful" class="text-lg font-medium text-gray-900">Thank you for subscribing</h3>
                <h3 v-else class="text-lg font-medium text-gray-900">Subscribe to meeting updates</h3>
                <button
                    @click="$emit('close')"
                    class="text-gray-600 hover:text-gray-700"
                >
                    <font-awesome :icon="['fas', 'x']" class="w-3.5 h-3.5" />
                </button>
            </div>
            <div v-if="submissionSuccessful" class=" max-h-[80vh] overflow-y-auto">
                <div class="px-6 py-6">
                    <p class="mb-2">Please check your email to confirm your subscription. Once confirmed, you’ll begin receiving notifications for the committees you’ve selected.</p>
                </div>
                <div class="border-t border-gray-300">
                    <div class="flex justify-end p-4 gap-2">
                        <Button type="secondary" @click="$emit('close')">
                            OK
                        </Button>
                    </div>
                </div>
            </div> 
            <div v-else class=" max-h-[80vh] overflow-y-auto">
                <div class="px-6 py-6">
                    <p class="mb-2">Choose committees below to subscribe to updates.</p>
                    <p>You’ll receive notifications when the committee adds a meeting and when the agenda for a committee’s meeting is published.</p>
                    <div class="flex flex-col gap-1 my-8">
                        <label class="text-gray-900 font-semibold">Email address <span class="text-danger text-sm font-normal">*</span></label>
                        <input
                            type="email"
                            placeholder="Enter email"
                            @input="emailTouched = null"
                            class="w-full md:w-1/2 rounded-lg px-4 py-2.5
                               text-gray-900 placeholder-gray-500
                               focus:outline-none
                               transition"
                            :class="emailError ? 'border border-danger' : 'border border-gray-400 focus:ring-2 focus:ring-blue-200 focus:border-blue-400'"
                            v-model="email"
                        />
                        <span v-if="emailError" class="text-danger text-sm">{{ emailError }}</span>
                    </div>
                    <div class="w-full border border-gray-400 bg-gray-200 rounded-lg flex items-center px-5 py-3 justify-between mt-8 mb-6">
                            <span class="flex items-center gap-x-3 text-gray-700">
                                <font-awesome :icon="['fas', 'circle-info']" class="h-5 w-5" />
                                <p>Committees without published meetings will not show in the list.</p>
                            </span>
                    </div>
                    <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-2">
                        <label
                            class="flex items-center gap-2 cursor-pointer select-none"
                        >
                            <input
                                type="checkbox"
                                class="h-4 w-4 rounded border border-gray-300 text-blue-600 focus:ring-blue-500"
                                :checked="isAllSelected"
                                @change="toggleAll"
                                v-model="allGroups"
                            />
                            <span>All</span>
                        </label>
                        <label
                            v-for="group in groups"
                            :key="group.id"
                            class="flex items-center gap-2 cursor-pointer select-none"
                        >
                            <input
                                type="checkbox"
                                class="h-4 w-4 rounded border border-gray-300 text-blue-600 focus:ring-blue-500"
                                :value="group.id"
                                v-model="selectedGroups"
                            />

                            <span>{{ group.name }}</span>
                        </label>
                    </div>
                    <span v-if="groupError" class="text-danger text-sm">{{ groupError }}</span>
                </div>
                <div class="border-t border-gray-300">
                    <div class="flex justify-end p-4 gap-2">
                        <Button type="secondary" @click="$emit('close')">
                            Cancel
                        </Button>
                        <Button type="primary"
                                @click="subscribe">
                            Subscribe
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    </VueFinalModal>
</template>

<script setup>
import { VueFinalModal } from 'vue-final-modal';
import {publicFetch} from "~/composables/publicFetch.js";
import {ref} from "vue";
import Button from "~/components/ui/Button.vue";

const props = defineProps({
    welcomeMessage: {
        type: String,
        required: true
    }
});
defineEmits(['close']);

const groups = ref([])
const selectedGroups = ref([])
const email = ref('')
const allGroups = ref(false)
const emailTouched = ref(false)
const groupTouched = ref(false)
const submissionSuccessful = useState('subscribeSubmissionSuccessful', () => false)

const isAllSelected = computed(() => {
    return groups.value.length > 0 && selectedGroups.value.length === groups.value.length
})

const toggleAll = () => {
    if (isAllSelected.value) {
        selectedGroups.value = []     // unselect all
    } else {
        selectedGroups.value = groups.value.map(g => g.id) // select all
    }
}


const fetchGroups = async () => {
    let response = null;
    try {
        response = await publicFetch(`/public-sites/subscribe/active-groups`);
        if(response.data){
            groups.value = response.data
        }
    }
    catch (e) {
        console.log(e);
    }
}

const subscribe = async () => {
    emailTouched.value = true;
    groupTouched.value = true;

    if(emailError.value || groupError.value){
        return;
    }

    let response = null;
    try {
        const payload = {
            email: email.value,
            selectedGroups: [...selectedGroups.value], // make sure it's a plain array
            allGroups: allGroups.value,
        }

        response = await publicFetch(`/public-sites/subscribe`, {
            method: 'POST',
            body: payload,
        })

        submissionSuccessful.value = true;

        if(response.data){
            //groups.value = response.data
        }

    } catch (error) {
        console.error('Failed to subscribe:', error)
    }
}

const emailError = computed(() => {
    if (!emailTouched.value) return null // No error on load
    if (!email.value) return "Email is required.";
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!regex.test(email.value)) return "Please enter a valid email.";
    return "";
});

const groupError = computed(() => {
    if (!groupTouched.value) return null // No error on load
    if (selectedGroups.value.length === 0) {
        return "Please select at least one committee.";
    }
    return "";
});


onMounted(async () => { fetchGroups(); });

</script>
